import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getGameplayAccess } from '../api/gameplay';
import { buildTrackedAuthRedirectUrl, getUserId } from '../analytics';
import { useGameStartAccountOffer } from './GameStartAccountOfferContext';
import { ACCESS_MESSAGES, GAMEPLAY_ACCESS_EVENT, isGameplayAccessReason, resetGameplayAccessOwner } from '../utils/gameplayAccess';

const GameplayAccessContext = createContext(null);

export const GameplayAccessProvider = ({ children }) => {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { cancelPendingOffer } = useGameStartAccountOffer();
  const [target, setTarget] = useState(null);
  const [state, setState] = useState({ target: null, access: null, error: null });
  const requestVersion = useRef(0);
  const currentRef = useRef(null);
  const redirectedRef = useRef(null);
  const owner = `${isLoaded}:${isSignedIn}:${userId || getUserId()}`;
  currentRef.current = { isLoaded, isSignedIn, location, owner, target };

  useLayoutEffect(() => {
    resetGameplayAccessOwner();
    requestVersion.current += 1;
    redirectedRef.current = null;
    setState({ target: null, access: null, error: null });
  }, [owner]);

  const redirect = useCallback((reason) => {
    const current = currentRef.current;
    if (!current.isLoaded || current.isSignedIn || !current.target || !isGameplayAccessReason(reason)) return;
    const nextPath = current.location.pathname + current.location.search + current.location.hash;
    if (redirectedRef.current === `${nextPath}:${reason}`) return;
    redirectedRef.current = `${nextPath}:${reason}`;
    cancelPendingOffer();
    navigate(buildTrackedAuthRedirectUrl({
      cta_kind: 'sign_up', source_surface: current.location.state?.auth_origin === 'game_archive' ? 'game_archive' : 'game_route_wrapper',
      gate_reason: reason, next_path: nextPath, message: ACCESS_MESSAGES[reason],
      game_slug: current.target.gameSlug, track_gate_viewed: true, trigger: 'anonymous_access_redirect',
    }), { replace: true });
  }, [navigate, cancelPendingOffer]);

  const refresh = useCallback(async () => {
    const current = currentRef.current;
    if (!current.isLoaded || current.isSignedIn || !current.target) return;
    const version = ++requestVersion.current;
    const result = await getGameplayAccess(current.target.puzzleInstanceId);
    if (version !== requestVersion.current || currentRef.current.owner !== current.owner || currentRef.current.target !== current.target) return;
    if (!result.success || !result.access) {
      setState({ target: current.target, access: null, error: 'Non è stato possibile caricare la partita. Riprova.' });
      return;
    }
    setState({ target: current.target, access: result.access, error: null });
    if (!result.access.can_play) redirect(result.access.reason);
  }, [redirect]);

  useEffect(() => {
    requestVersion.current += 1;
    redirectedRef.current = null;
    refresh();
  }, [target, owner, refresh]);

  useEffect(() => {
    const handleResponse = (event) => {
      if (event.detail.response.success === false && isGameplayAccessReason(event.detail.response.error)) {
        setState({ target: currentRef.current.target, access: event.detail.response.access, error: null });
        redirect(event.detail.response.error);
      } else refresh();
    };
    const handleVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener(GAMEPLAY_ACCESS_EVENT, handleResponse);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener(GAMEPLAY_ACCESS_EVENT, handleResponse);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refresh, redirect]);

  useEffect(() => {
    if (!state.access || !target || isSignedIn) return undefined;
    const { expires_at: expiresAt, next_available_at: nextAvailableAt, server_time: serverTime } = state.access;
    const boundaries = [expiresAt, nextAvailableAt].filter(Boolean).map(Date.parse).filter((value) => value > Date.parse(serverTime));
    if (!boundaries.length) return undefined;
    const delay = Math.min(...boundaries) - Date.parse(serverTime);
    const timer = setTimeout(() => {
      if (delay <= 2147483647 && expiresAt && Date.parse(expiresAt) <= Date.parse(serverTime) + delay) {
        setState((previous) => ({ ...previous, access: { ...previous.access, can_play: false } }));
        redirect('ANONYMOUS_TRIAL_EXPIRED');
      } else refresh();
    }, Math.min(delay, 2147483647));
    return () => clearTimeout(timer);
  }, [state, target, isSignedIn, redirect, refresh]);

  const value = useMemo(() => ({ setTarget, state, refresh, isLoaded, isSignedIn }), [state, refresh, isLoaded, isSignedIn]);
  return <GameplayAccessContext.Provider value={value}>{children}</GameplayAccessContext.Provider>;
};

export const useGameplayAccess = () => useContext(GameplayAccessContext);
